import {
  STAKES,
  STAKE_TIERS,
  VENUES,
  type GameId,
  type KuboResponse,
  type LobbyResponse,
  type StakeTier,
} from '@tira/protocol';
import { useEffect, useState, type JSX } from 'react';
import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { Sheet } from '../components/Sheet.js';
import { useLang, useT } from '../i18n/index.js';
import { api } from '../net/api.js';
import { socket } from '../net/socket.js';
import { useApp } from '../state/store.js';
import { band, chips } from './lib/format.js';
import { useSeatWait } from './lib/waiting.js';
import { Screen } from './parts/Screen.js';
import './Kubo.css';

const CODE_LENGTH = 6;

/**
 * S16 — private rooms. Two honest limits are stated on this screen rather than
 * discovered later: Kubo caps at Bente (PRD §7.4 — a private table is the
 * obvious place to dump chips on purpose, so the high tier stays public), and
 * nothing in this app can move chips from one wallet to another anyway.
 */
export function Kubo(): JSX.Element {
  const me = useApp((s) => s.me);
  const table = useApp((s) => s.table);
  const match = useApp((s) => s.match);
  const balance = useApp((s) => s.balance);
  const setToast = useApp((s) => s.setToast);
  const t = useT();

  const [game, setGame] = useState<GameId>('dama');
  const [tier, setTier] = useState<StakeTier>('barya');
  const [buyIn, setBuyIn] = useState(STAKES.barya.minBuyIn);
  const [code, setCode] = useState('');
  const [made, setMade] = useState<KuboResponse | null>(null);
  const [invite, setInvite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lobby, setLobby] = useState<LobbyResponse | null>(null);
  const [lobbyDown, setLobbyDown] = useState(false);

  // Same source of truth as the compound: a venue the server has not opened
  // cannot be opened privately either.
  useEffect(() => {
    let alive = true;
    api
      .lobby()
      .then((r) => {
        if (!alive) return;
        setLobby(r);
        setLobbyDown(false);
      })
      .catch(() => {
        if (alive) setLobbyDown(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  /**
   * Until the lobby answers, nothing is playable: creating a kubo seats the
   * host and escrows the buy-in in the same request, so guessing a venue open
   * costs chips at a table that can never deal. The server's `available` is the
   * whole answer — it comes from the engine registry, so a venue opens the day
   * its rules ship. Anding it with the wave number kept shipped venues shut.
   */
  const playable = (g: GameId): boolean =>
    lobby?.venues.find((v) => v.game === g)?.available ?? false;

  const def = STAKES[tier];
  const short = balance < buyIn;
  const inKubo = table?.kuboCode ? table : null;
  const isHost = inKubo !== null && inKubo.hostUserId === me?.userId;

  /*
   * The host waits here, not on the table screen — the route only changes when
   * the hand deals. So the two things that make an empty room survivable have
   * to be on this screen as well: what the wait ends in, and a way to end it
   * now. Without them a host who opened a kubo and sat down saw six words about
   * a code and nothing about the bots already on their way.
   */
  const wait = useSeatWait(inKubo, useLang());
  const canFill = inKubo !== null && match === null && wait.empty > 0;

  const pickTier = (t: StakeTier) => {
    setTier(t);
    setBuyIn(STAKES[t].minBuyIn);
  };

  const create = () => {
    setBusy(true);
    api
      .createKubo(game, tier, buyIn)
      .then((r) => {
        setBusy(false);
        setMade(r);
        setInvite(true);
      })
      .catch((e: Error) => {
        setBusy(false);
        setToast(t('results.kubo.err.create', { error: e.message }), 'warn');
      });
  };

  const join = () => {
    const clean = code.trim().toUpperCase();
    if (clean.length !== CODE_LENGTH) {
      setToast(t('results.kubo.err.codelen'), 'warn');
      return;
    }
    setBusy(true);
    api
      .joinKubo(clean)
      .then((r) => {
        setBusy(false);
        setMade(r);
        setToast(t('results.kubo.ok.joined'), 'info');
      })
      .catch((e: Error) => {
        setBusy(false);
        setToast(t('results.kubo.err.join', { error: e.message }), 'warn');
      });
  };

  const copyCode = (value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => setToast(t('results.kubo.ok.copied'), 'info'))
      .catch(() => setToast(t('results.kubo.err.copy', { code: value }), 'warn'));
  };

  return (
    <Screen title={t('results.kubo.title')} sub={t('results.kubo.sub')}>
      {inKubo ? (
        <section className="s-panel">
          <div className="s-panel__head">
            <h2>{t('results.kubo.in.head')}</h2>
            <p>
              {t('results.kubo.in.line', {
                game: VENUES[inKubo.game].name,
                tier: t(`results.stake.${inKubo.tier}`),
                n: chips(inKubo.buyIn),
              })}
            </p>
          </div>
          <div className="s-actions">
            <div className="kubo__code">
              <span>{t('results.kubo.code.label')}</span>
              <b>{inKubo.kuboCode}</b>
            </div>
            <Button variant="secondary" onClick={() => copyCode(inKubo.kuboCode ?? '')}>
              {t('results.kubo.code.copy')}
            </Button>
          </div>

          <div className="kubo__seats">
            {inKubo.seats.map((seat, i) => (
              <div className="kubo__seat" key={i} data-empty={seat === null}>
                <Icon name={seat ? 'chip' : 'kubo'} size={18} />
                {seat ? (
                  <>
                    <b>{seat.handle}</b>
                    {seat.isBot ? <span className="s-tag">{t('results.ai')}</span> : null}
                    {seat.userId === inKubo.hostUserId ? (
                      <span className="s-tag s-tag--gold">{t('results.kubo.host')}</span>
                    ) : null}
                  </>
                ) : (
                  <span>{t('results.kubo.seat.empty')}</span>
                )}
              </div>
            ))}
          </div>

          {canFill ? <p className="kubo__wait">{wait.line}</p> : null}

          <p className="s-note">
            {isHost ? t('results.kubo.note.host') : t('results.kubo.note.guest')}
          </p>

          <div className="s-actions">
            {canFill ? (
              <Button
                variant="primary"
                onClick={() => socket.send({ t: 'table.bots', tableId: inKubo.tableId })}
              >
                {t('results.kubo.fill', { n: wait.empty })}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => socket.send({ t: 'table.leave', tableId: inKubo.tableId })}>
              {t('results.kubo.leave')}
            </Button>
          </div>
        </section>
      ) : null}

      <div className="s-cols">
        <section className="s-panel">
          <div className="s-panel__head">
            <h2>{t('results.kubo.make.head')}</h2>
            <p>{t('results.kubo.make.sub')}</p>
          </div>

          <div className="s-field">
            <label>{t('results.kubo.field.game')}</label>
            <div className="kubo__games">
              {(Object.keys(VENUES) as GameId[]).map((g) => {
                const open = playable(g);
                return (
                  <button
                    key={g}
                    className="s-tag"
                    aria-pressed={game === g}
                    disabled={!open}
                    style={{
                      borderColor: game === g ? 'var(--c-tansan)' : undefined,
                      color: !open ? 'var(--ink-faint)' : game === g ? 'var(--c-tansan)' : undefined,
                    }}
                    onClick={() => setGame(g)}
                  >
                    {open
                      ? t(`venue.${g}`)
                      : t('results.kubo.game.soon', { game: t(`venue.${g}`) })}
                  </button>
                );
              })}
            </div>
            {lobbyDown ? (
              <span className="s-note">{t('results.kubo.lobby.down')}</span>
            ) : lobby === null ? (
              <span className="s-note">{t('results.kubo.lobby.loading')}</span>
            ) : null}
          </div>

          <div className="s-field">
            <label>{t('results.kubo.field.stake')}</label>
            <div className="s-segment">
              {STAKE_TIERS.map((tr) => (
                <button
                  key={tr}
                  aria-pressed={tier === tr}
                  disabled={!STAKES[tr].kuboAllowed}
                  onClick={() => pickTier(tr)}
                >
                  {t(`results.stake.${tr}`)}
                </button>
              ))}
            </div>
          </div>

          {/* The cap is stated where the player runs into it, with the reason. */}
          <p className="kubo__why">
            <Icon name="responsible" size={18} />
            <span>
              <b>{t('results.kubo.why.q')}</b> {t('results.kubo.why.body')}
            </span>
          </p>

          <div className="s-field">
            <label htmlFor="kubo-buyin">
              {t('results.kubo.field.buyin', { band: band(def.minBuyIn, def.maxBuyIn) })}
            </label>
            <input
              id="kubo-buyin"
              className="s-range"
              type="range"
              min={def.minBuyIn}
              max={def.maxBuyIn}
              step={Math.max(100, Math.round((def.maxBuyIn - def.minBuyIn) / 10))}
              value={buyIn}
              onChange={(e) => setBuyIn(Number(e.currentTarget.value))}
            />
            <span className="s-note">
              {t('results.kubo.perseat', { n: chips(buyIn), bal: chips(balance) })}
            </span>
          </div>

          {short ? (
            <p className="s-note" style={{ color: '#f0906f' }}>
              {t('results.kubo.short', { n: chips(buyIn - balance) })}
            </p>
          ) : null}

          <div className="s-actions">
            <Button variant="primary" disabled={busy || short || !playable(game)} onClick={create}>
              {t('results.kubo.create')}
            </Button>
          </div>
        </section>

        <section className="s-panel">
          <div className="s-panel__head">
            <h2>{t('results.kubo.join.head')}</h2>
            <p>{t('results.kubo.join.sub')}</p>
          </div>
          <div className="kubo__join">
            <div className="s-field">
              <label htmlFor="kubo-code">{t('results.kubo.field.code')}</label>
              <input
                id="kubo-code"
                className="s-input"
                inputMode="text"
                autoCapitalize="characters"
                maxLength={CODE_LENGTH}
                placeholder="ABC123"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              />
            </div>
            <Button variant="primary" disabled={busy || code.length !== CODE_LENGTH} onClick={join}>
              {t('results.kubo.join')}
            </Button>
          </div>
          <p className="s-note">{t('results.kubo.join.note')}</p>
        </section>
      </div>

      <Sheet
        open={invite && made !== null}
        onClose={() => setInvite(false)}
        title={t('results.kubo.invite.title')}
        footnote={t('results.disclosure')}
      >
        {made ? (
          <div className="confirm">
            <div className="kubo__code">
              <span>{t('results.kubo.code.label')}</span>
              <b>{made.code}</b>
            </div>
            <p className="confirm__body">
              {t('results.kubo.invite.body', {
                game: t(`venue.${game}`),
                tier: t(`results.stake.${tier}`),
                n: chips(buyIn),
              })}
            </p>
            <div className="s-actions">
              <Button variant="primary" onClick={() => copyCode(made.code)}>
                {t('results.kubo.code.copy')}
              </Button>
              <Button variant="ghost" onClick={() => setInvite(false)}>
                {t('results.kubo.invite.done')}
              </Button>
            </div>
          </div>
        ) : null}
      </Sheet>
    </Screen>
  );
}
